export type CityReview = {
  name: string;
  neighborhood: string;
  rating: number;
  text: string;
};

export type CityFaq = {
  q: string;
  a: string;
};

export type CityInstaller = {
  name: string;
  years: string; // bv. "3,5 jaar"
  experience: string; // bv. "21 jaar ervaring met groepenkasten"
  bio1: string;
  bio2: string;
};

export type CityContent = {
  name: string;
  metaTitle: string;
  metaDescription: string;
  heroIntro: string;
  body: string[];
  signals: string[];
  neighborhoods: string[];
  reviews: CityReview[];
  localFaq?: CityFaq[];
  installer?: CityInstaller;
};

export const CITY_CONTENT = {
  amsterdam: {
    name: "Amsterdam",
    metaTitle: "Groepenkast vervangen Amsterdam | Elvion Group",
    metaDescription:
      "Groepenkast vervangen in Amsterdam door erkende NEN 1010-installateurs. Vaste vanaf-prijs, 15 jaar garantie op AEG en offerte binnen 24 uur.",
    heroIntro:
      "Van grachtenpand tot IJburg: onze erkende installateurs vervangen dagelijks groepenkasten in heel Amsterdam. Veilig, snel en klaar voor laadpaal, warmtepomp en zonnepanelen.",
    body: [
      "In Amsterdam staan we voor bijzondere elektrische installaties. Veel oude woningen in de binnenstad hebben nog een verouderde groepenkast met schroefzekeringen en zonder aardlekschakelaar. Dat is niet alleen onveilig, het beperkt ook uw mogelijkheden om moderne apparaten zoals inductie, warmtepomp of laadpaal aan te sluiten.",
      "Onze installateurs kennen de Amsterdamse woningvoorraad: van 17e-eeuwse grachtenpanden met beperkte kabelruimte tot appartementen in Zuidas en nieuwbouw in IJburg. Wij vervangen uw groepenkast in één werkdag, altijd volgens NEN 1010 en met zegelrecht bij Liander, zodat u geen extra kosten heeft voor herverzegeling.",
      "Betaalt u nu nog met vaste-lastenverhoging omdat uw meterkast niet klaar is voor zonnepanelen of een elektrische auto? Dan is groepenkast vervangen in Amsterdam vaak binnen enkele jaren terugverdiend via lagere netbeheerkosten en energiebesparing.",
    ],
    signals: [
      "Uw groepenkast heeft nog schroef- of smeltzekeringen",
      "Er is geen (of maar één) aardlekschakelaar aanwezig",
      "U wilt een laadpaal, warmtepomp of inductiekookplaat installeren",
      "De hoofdschakelaar slaat regelmatig af",
      "Er zijn brandsporen, roet of een schroeilucht bij de kast",
      "U gaat verbouwen of een dakopbouw plaatsen in Amsterdam",
    ],
    neighborhoods: [
      "Centrum", "De Pijp", "Oud-Zuid", "Oud-West", "Amsterdam-Noord",
      "IJburg", "Zuidas", "Osdorp", "Amstelveen", "Diemen",
    ],
    reviews: [
      { name: "Familie de Vries", neighborhood: "De Pijp", rating: 5, text: "Oude kast met schroefzekeringen vervangen door een moderne AEG-groepenkast. Netjes gewerkt, alles binnen één dag klaar en de zegels van Liander waren geen probleem." },
      { name: "Mark B.", neighborhood: "IJburg", rating: 5, text: "Groepenkast voorbereid op laadpaal en zonnepanelen. Advies was helder, prijs vooraf duidelijk. Aanrader." },
      { name: "Sanne K.", neighborhood: "Amsterdam-Noord", rating: 5, text: "Snel gepland, monteur was vriendelijk en de nieuwe kast ziet er strak uit. 15 jaar garantie op AEG geeft rust." },
    ],
    localFaq: [
      { q: "Regelen jullie het zegelrecht bij Liander?", a: "Ja. Wij zijn zegelbevoegd bij Liander (netbeheerder in Amsterdam), dus u betaalt geen extra kosten voor herverzegeling van de hoofdaansluiting." },
      { q: "Kunnen jullie werken in een grachtenpand met smalle kabelruimte?", a: "Zeker. We hebben veel ervaring met monumentale panden en werken zorgvuldig, met respect voor bestaande afwerking en bekabeling." },
    ],
    installer: {
      name: "Maikel",
      years: "3,5 jaar",
      experience: "21 jaar ervaring met groepenkasten",
      bio1: "Al 3,5 jaar onderdeel van ons team. Maikel groeide op in Amsterdam-Oost en kent de stad als geen ander, van de smalle trapopgangen in de grachtengordel tot de nieuwbouw op IJburg.",
      bio2: "\"De ene dag werk ik aan een monumentaal pand in de Jordaan, de andere dag in een moderne loft aan de Zuidas. Geen dag is hetzelfde, en dat maakt dit vak zo leuk.\"",
    },
  },

  rotterdam: {
    name: "Rotterdam",
    metaTitle: "Groepenkast vervangen Rotterdam | Elvion Group",
    metaDescription:
      "Groepenkast vervangen in Rotterdam door erkende NEN 1010-installateurs. Vaste vanaf-prijs, 15 jaar garantie op AEG en offerte binnen 24 uur.",
    heroIntro:
      "Van vooroorlogse woningen in Kralingen tot nieuwbouw op de Kop van Zuid: onze installateurs vervangen dagelijks groepenkasten in heel Rotterdam. Veilig, volgens NEN 1010 en klaar voor laadpaal en warmtepomp.",
    body: [
      "Rotterdam kent een enorm gevarieerde woningvoorraad. In wijken als Kralingen en Hillegersberg vinden we regelmatig groepenkasten uit de jaren '60 en '70, terwijl in Nesselande en op de Kop van Zuid vaak juist een uitbreiding richting 3-fase nodig is voor moderne apparatuur.",
      "Onze monteurs zijn zegelbevoegd bij Stedin, de netbeheerder in Rotterdam. Dat betekent dat u geen aparte kosten heeft voor herverzegeling van de hoofdaansluiting. We vervangen uw groepenkast doorgaans binnen één werkdag, met keuring en oplevering volgens NEN 1010.",
      "Wilt u zonnepanelen, een laadpaal of warmtepomp aansluiten? Dan is een moderne AEG-kast met aardlekautomaten per groep de veilige basis. We bereiden de kast standaard voor op uitbreiding, zodat u later geen dubbel werk krijgt.",
    ],
    signals: [
      "Uw groepenkast heeft nog schroef- of smeltzekeringen",
      "Er is geen aardlekschakelaar of maar één centrale",
      "U wilt een laadpaal of warmtepomp in Rotterdam plaatsen",
      "De hoofdschakelaar slaat regelmatig af",
      "Brandplekken, roet of een schroeilucht rondom de kast",
      "U verbouwt of gaat uitbreiden met een dakopbouw",
    ],
    neighborhoods: [
      "Centrum", "Kralingen", "Hillegersberg", "Kop van Zuid", "Delfshaven",
      "Nesselande", "Prins Alexander", "Ommoord", "Charlois", "Feijenoord",
    ],
    reviews: [
      { name: "Familie Willemsen", neighborhood: "Kralingen", rating: 5, text: "Verouderde kast met porseleinen zekeringen keurig vervangen door een AEG-kast. Alles op één dag, netjes achtergelaten en duidelijke uitleg." },
      { name: "Jeroen H.", neighborhood: "Kop van Zuid", rating: 5, text: "Van 1-fase naar 3-fase omgezet i.v.m. laadpaal en inductie. Vaste prijs vooraf, geen gedoe met Stedin." },
      { name: "Anouk R.", neighborhood: "Hillegersberg", rating: 5, text: "Snel ingepland en netjes uitgevoerd. Monteur nam echt de tijd om alles uit te leggen." },
    ],
    localFaq: [
      { q: "Regelen jullie het zegelrecht bij Stedin?", a: "Ja. Wij zijn zegelbevoegd bij Stedin (netbeheerder in Rotterdam), dus u betaalt geen extra kosten voor herverzegeling." },
      { q: "Werken jullie ook in appartementen met een centrale meterkast?", a: "Ja, we werken regelmatig in flats en appartementencomplexen in Rotterdam. We stemmen zo nodig af met de VvE of beheerder." },
    ],
    installer: {
      name: "Danny",
      years: "2 jaar",
      experience: "Ruime ervaring met groepenkasten",
      bio1: "Danny is geboren en getogen in Rotterdam-Zuid en kwam 2 jaar geleden bij ons team, na een carrière bij Defensie. Zijn technische achtergrond en discipline zet hij nu in voor veilige groepenkasten in heel Rotterdam.",
      bio2: "\"Bij Defensie leerde ik dat veiligheid en precisie altijd op nummer één staan. Die mentaliteit neem ik mee in elke meterkast die ik vervang.\"",
    },
  },

  "den-haag": {
    name: "Den Haag",
    metaTitle: "Groepenkast vervangen Den Haag | Elvion Group",
    metaDescription:
      "Groepenkast vervangen in Den Haag door erkende NEN 1010-installateurs. Vaste vanaf-prijs, 15 jaar garantie op AEG en offerte binnen 24 uur.",
    heroIntro:
      "Van herenhuizen in Statenkwartier tot appartementen in Ypenburg: onze installateurs vervangen groepenkasten in heel Den Haag. Veilig, snel en volgens de laatste NEN 1010-norm.",
    body: [
      "Den Haag kent een groot aandeel vooroorlogse woningen, met name in wijken als Bezuidenhout, Statenkwartier en Duinoord. Daar vinden we vaak nog originele meterkasten met smeltzekeringen die niet meer voldoen aan de huidige eisen.",
      "Onze installateurs werken volgens NEN 1010 en zijn zegelbevoegd bij Stedin. We vervangen de kast doorgaans in één werkdag, met beperkte spanningsloze tijd en zonder rommel. U ontvangt vooraf een vaste prijs en achteraf een keuringsrapport.",
      "Wilt u de meterkast klaar maken voor een laadpaal in de straat of zonnepanelen op het dak? Dan zorgen we voor voldoende reserveruimte in de nieuwe AEG-kast, zodat uitbreiden later eenvoudig is.",
    ],
    signals: [
      "Kast met schroef- of smeltzekeringen aanwezig",
      "Geen aardlekschakelaar of slechts één centrale ALS",
      "Plannen voor laadpaal, warmtepomp of inductie",
      "Hoofdschakelaar slaat regelmatig af",
      "Roet, brandsporen of een vreemde geur bij de kast",
      "Verbouwing of uitbreiding in Den Haag",
    ],
    neighborhoods: [
      "Centrum", "Bezuidenhout", "Statenkwartier", "Duinoord", "Scheveningen",
      "Ypenburg", "Leidschenveen", "Loosduinen", "Rijswijk", "Voorburg",
    ],
    reviews: [
      { name: "Familie Bakker", neighborhood: "Statenkwartier", rating: 5, text: "Prachtig oud herenhuis, maar de meterkast was echt aan vervanging toe. Alles keurig binnen een dag opgeleverd inclusief keuring." },
      { name: "Peter L.", neighborhood: "Ypenburg", rating: 5, text: "3-fase kast geplaatst met voorbereiding voor laadpaal. Duidelijke offerte, geen verrassingen." },
      { name: "Mirjam D.", neighborhood: "Scheveningen", rating: 5, text: "Fijne monteur, netjes gewerkt en goed uitgelegd wat er precies gebeurde." },
    ],
    localFaq: [
      { q: "Zijn jullie zegelbevoegd bij Stedin in Den Haag?", a: "Ja. Wij mogen de hoofdverzegeling van Stedin verwijderen en opnieuw plaatsen, u betaalt daar geen apart bedrag voor." },
      { q: "Werken jullie ook in monumentale panden in het centrum?", a: "Ja. We hebben ervaring met monumentale panden en werken zorgvuldig om bestaande afwerking te sparen." },
    ],
    installer: {
      name: "Rick",
      years: "4 jaar",
      experience: "16 jaar ervaring met elektrotechniek",
      bio1: "Rick werkt al 4 jaar in ons team en is opgegroeid in de omgeving van Den Haag. Hij voelt zich net zo thuis in een jaren-'30 woning in Bezuidenhout als in een moderne nieuwbouwwoning in Ypenburg.",
      bio2: "\"Ik vind het mooi om ouderwetse kasten om te bouwen tot een moderne installatie die weer decennia meekan, en die klaar is voor zonnepanelen en een laadpaal.\"",
    },
  },

  utrecht: {
    name: "Utrecht",
    metaTitle: "Groepenkast vervangen Utrecht | Elvion Group",
    metaDescription:
      "Groepenkast vervangen in Utrecht door erkende NEN 1010-installateurs. Vaste vanaf-prijs, 15 jaar garantie op AEG en offerte binnen 24 uur.",
    heroIntro:
      "Van jaren-'30 woning in Tuinwijk tot nieuwbouw in Leidsche Rijn: wij vervangen dagelijks groepenkasten in heel Utrecht. Veilig, snel en klaar voor de energietransitie.",
    body: [
      "Utrecht groeit snel, en veel woningen die 20 of 30 jaar geleden ruim voldeden, lopen nu tegen de grenzen van hun meterkast aan. Zeker met inductiekoken, elektrisch laden en warmtepompen wordt de vraag naar vermogen groter.",
      "Onze installateurs werken in heel Utrecht, van de binnenstad tot Leidsche Rijn en Vleuten. We werken conform NEN 1010, zijn zegelbevoegd bij Stedin en leveren de installatie op met keuringsrapport en 15 jaar garantie op AEG.",
      "Bij een vervanging zorgen we standaard voor voldoende reserve, zodat uitbreiden later niet direct opnieuw hakwerk betekent. Denk aan een groep voor een toekomstige laadpaal of extra circuits voor zonnepanelen.",
    ],
    signals: [
      "Meterkast met schroef- of smeltzekeringen",
      "Geen aardlekschakelaar of maar één centrale",
      "Wens voor laadpaal, warmtepomp of zonnepanelen",
      "Hoofdschakelaar valt regelmatig uit",
      "Brandsporen of schroeilucht bij de kast",
      "Verbouwing of uitbreiding gepland",
    ],
    neighborhoods: [
      "Binnenstad", "Tuinwijk", "Wittevrouwen", "Oog in Al", "Kanaleneiland",
      "Overvecht", "Lunetten", "Leidsche Rijn", "Vleuten", "De Meern",
    ],
    reviews: [
      { name: "Familie Jansen", neighborhood: "Tuinwijk", rating: 5, text: "Oude kast uit '68 vervangen. Alles verliep zoals afgesproken, monteur was op tijd en werkte netjes." },
      { name: "Bas V.", neighborhood: "Leidsche Rijn", rating: 5, text: "3-fase kast met voorbereiding voor laadpaal. Prima advies vooraf, prijs helder en op tijd geleverd." },
      { name: "Lisa M.", neighborhood: "Wittevrouwen", rating: 5, text: "Snel gepland, correcte communicatie en uitstekend werk. Zeker een aanrader." },
    ],
    localFaq: [
      { q: "Zijn jullie zegelbevoegd bij Stedin in Utrecht?", a: "Ja. Wij zijn zegelbevoegd bij Stedin, dus u heeft geen extra kosten voor herverzegeling van de hoofdaansluiting." },
      { q: "Kunnen jullie de kast voorbereiden op een laadpaal in Leidsche Rijn?", a: "Ja. We plaatsen reserveruimte en, indien gewenst, direct een dedicated groep zodat de laadpaal later eenvoudig aan te sluiten is." },
    ],
    installer: {
      name: "Tim",
      years: "2 jaar",
      experience: "4 jaar ervaring in de elektrotechniek",
      bio1: "Tim werkt al 2 jaar in ons team en woont zelf in de regio Utrecht. Hij werkt net zo lief in een klein monumentaal pandje in Wittevrouwen als in een grote gezinswoning in Leidsche Rijn.",
      bio2: "\"Een goede meterkast is de basis van je woning. Ik vind het belangrijk dat mensen begrijpen wat er gebeurt, dus ik neem altijd de tijd om het rustig uit te leggen.\"",
    },
  },

  eindhoven: {
    name: "Eindhoven",
    metaTitle: "Groepenkast vervangen Eindhoven | Elvion Group",
    metaDescription:
      "Groepenkast vervangen in Eindhoven door erkende NEN 1010-installateurs. Vaste vanaf-prijs, 15 jaar garantie op AEG en offerte binnen 24 uur.",
    heroIntro:
      "Van Philipswoningen in Woensel tot moderne appartementen in Strijp-S: wij vervangen dagelijks groepenkasten in heel Eindhoven. Veilig, snel en NEN 1010-conform.",
    body: [
      "In Eindhoven staan veel typische Philipswoningen uit de jaren '30 tot '50 met verouderde installaties. Deze woningen zijn vaak nog uitgerust met een 1-fase aansluiting en oude schroefzekeringen, wat problemen geeft bij moderne apparatuur.",
      "Onze installateurs zijn actief in heel Eindhoven, van Woensel en Stratum tot Meerhoven en Strijp-S. We werken conform NEN 1010 en zijn zegelbevoegd bij Enexis, de netbeheerder in Noord-Brabant. Vervangen kan doorgaans in één werkdag.",
      "Wilt u overstappen naar 3-fase voor uw warmtepomp, laadpaal of inductie? We rekenen vooraf uit wat u nodig heeft en regelen indien gewenst ook de aanvraag bij Enexis voor een zwaardere aansluiting.",
    ],
    signals: [
      "Oude schroef- of smeltzekeringen in de meterkast",
      "Geen (of maar één) aardlekschakelaar aanwezig",
      "U wilt inductie, warmtepomp of laadpaal aansluiten",
      "Hoofdschakelaar slaat regelmatig af",
      "Warme kast, roet of brandsporen",
      "Verbouwing of uitbreiding van uw Eindhovense woning",
    ],
    neighborhoods: [
      "Centrum", "Woensel", "Stratum", "Tongelre", "Gestel",
      "Strijp-S", "Meerhoven", "Blixembosch", "Nuenen", "Waalre",
    ],
    reviews: [
      { name: "Familie Peeters", neighborhood: "Woensel", rating: 5, text: "Typische Philipswoning met een oude kast, netjes vervangen en meteen voorbereid op zonnepanelen. Top gedaan." },
      { name: "Ruud K.", neighborhood: "Strijp-S", rating: 5, text: "Duidelijke offerte, snel ingepland en vakkundig gemonteerd. Aanrader in Eindhoven." },
      { name: "Karin S.", neighborhood: "Meerhoven", rating: 5, text: "Van 1-fase naar 3-fase omgezet. Alles keurig geregeld met Enexis, geen omkijken naar." },
    ],
    localFaq: [
      { q: "Zijn jullie zegelbevoegd bij Enexis?", a: "Ja. Wij zijn zegelbevoegd bij Enexis, netbeheerder in Eindhoven en omstreken. U betaalt geen extra kosten voor herverzegeling." },
      { q: "Kunnen jullie ook de aanvraag voor verzwaring bij Enexis regelen?", a: "Ja. Bij een gewenste verzwaring van 1x25A naar 3x25A of hoger regelen wij de aanvraag bij Enexis en stemmen we de planning af." },
    ],
    installer: {
      name: "Jeffrey",
      years: "1 jaar",
      experience: "Veel materiaalkennis door technische groothandel",
      bio1: "Jeffrey werkt al 1 jaar in ons team. Dankzij zijn achtergrond bij een technische groothandel kent hij vrijwel ieder materiaal en component dat in een moderne groepenkast gebruikt wordt.",
      bio2: "\"In Eindhoven zie je duidelijk het verschil tussen oud en nieuw. Ik vind het gaaf om oude installaties klaar te maken voor de toekomst, zonnepanelen, warmtepomp, alles kan er straks op.\"",
    },
  },

  almere: {
    name: "Almere",
    metaTitle: "Groepenkast vervangen Almere | Elvion Group",
    metaDescription:
      "Groepenkast vervangen in Almere door erkende NEN 1010-installateurs. Vaste vanaf-prijs, 15 jaar garantie op AEG en offerte binnen 24 uur.",
    heroIntro:
      "Van de eerste woningen in Almere Haven tot de nieuwbouw in Poort en Duin: wij vervangen groepenkasten in heel Almere. Veilig, snel en klaar voor zonnepanelen en laadpaal.",
    body: [
      "Almere is een jonge stad, maar de eerste woningen in Almere Haven en Almere Stad naderen inmiddels de 45 jaar. Meterkasten uit die tijd voldoen vaak niet meer aan de huidige NEN 1010 en missen bijvoorbeeld voldoende aardlekbeveiliging.",
      "Onze installateurs werken in heel Almere, en zijn zegelbevoegd bij Liander (netbeheerder in Flevoland). We vervangen uw kast doorgaans in één werkdag, met vaste vooraf-prijs en een keuringsrapport bij oplevering.",
      "Veel Almeerders kiezen tegelijk voor een uitbreiding naar 3-fase, omdat de combinatie van zonnepanelen, warmtepomp en laadpaal snel meer vermogen vraagt dan een 1-fase aansluiting kan leveren.",
    ],
    signals: [
      "Kast met schroef- of smeltzekeringen",
      "Geen aardlekschakelaar of maar één centrale",
      "Plannen voor laadpaal, warmtepomp of zonnepanelen",
      "Hoofdschakelaar valt regelmatig uit",
      "Brandsporen, roet of schroeilucht bij de kast",
      "Verbouwing of uitbouw van uw Almeerse woning",
    ],
    neighborhoods: [
      "Almere Stad", "Almere Haven", "Almere Buiten", "Almere Poort", "Almere Hout",
      "Almere Duin", "Muziekwijk", "Stedenwijk", "Filmwijk", "Waterwijk",
    ],
    reviews: [
      { name: "Familie de Boer", neighborhood: "Almere Haven", rating: 5, text: "Woning uit de eerste bouwlichting, kast was echt aan vernieuwing toe. Alles binnen een dag geregeld en netjes opgeleverd." },
      { name: "Ferry M.", neighborhood: "Almere Poort", rating: 5, text: "Nieuwe AEG-kast voorbereid op laadpaal en zonnepanelen. Duidelijke prijs, geen gedoe." },
      { name: "Nadia K.", neighborhood: "Muziekwijk", rating: 5, text: "Snelle planning en correcte monteur. Alles nu weer veilig en overzichtelijk." },
    ],
    localFaq: [
      { q: "Wie is de netbeheerder in Almere?", a: "Liander is de netbeheerder in Flevoland. Wij zijn zegelbevoegd bij Liander, dus er zijn geen extra kosten voor herverzegeling." },
      { q: "Kan ik overstappen op 3-fase in Almere?", a: "Ja. Bij een combinatie van zonnepanelen, warmtepomp en laadpaal is 3-fase vaak verstandig. We rekenen dit vooraf voor u door." },
    ],
    installer: {
      name: "Sven",
      years: "3 jaar",
      experience: "5 jaar ervaring in de elektro en beveiligingstechniek",
      bio1: "Sven werkt 3 jaar in ons team en woont in Almere. Hij kent zowel de eerste woningen in Almere Haven als de nieuwste opleveringen in Poort en Duin uit z'n hoofd.",
      bio2: "\"Almere groeit hard en veel installaties vragen om een update. Ik vind het leuk om mensen te helpen hun huis klaar te maken voor zonnepanelen en een laadpaal.\"",
    },
  },

  tilburg: {
    name: "Tilburg",
    metaTitle: "Groepenkast vervangen Tilburg | Elvion Group",
    metaDescription:
      "Groepenkast vervangen in Tilburg door erkende NEN 1010-installateurs. Vaste vanaf-prijs, 15 jaar garantie op AEG en offerte binnen 24 uur.",
    heroIntro:
      "Van arbeiderswoningen in Oud-Noord tot nieuwbouw in de Reeshof: wij vervangen groepenkasten in heel Tilburg. Veilig, snel en volgens de laatste NEN 1010-norm.",
    body: [
      "Tilburg heeft een rijke industriële geschiedenis en veel woningen uit de jaren '30 tot '60, met name in wijken als Oud-Noord en Broekhoven. Daar vinden we regelmatig verouderde meterkasten met porseleinen zekeringen en zonder moderne aardlekbeveiliging.",
      "Onze installateurs zijn zegelbevoegd bij Enexis (netbeheerder in Tilburg) en werken volgens NEN 1010. We vervangen uw groepenkast doorgaans in één werkdag, met een vaste prijs vooraf en een keuringsrapport bij oplevering.",
      "Ook in de Reeshof en Berkel-Enschot komen we veel klanten tegen die de kast willen uitbreiden voor een warmtepomp of laadpaal. Bij vervanging voorzien we standaard reserveruimte, zodat dit later eenvoudig aan te sluiten is.",
    ],
    signals: [
      "Meterkast met schroef- of smeltzekeringen",
      "Geen (of maar één) aardlekschakelaar",
      "Wens voor laadpaal, warmtepomp of inductie",
      "Hoofdschakelaar slaat regelmatig af",
      "Roet, brandsporen of schroeilucht rond de kast",
      "Verbouwing of dakopbouw in uw Tilburgse woning",
    ],
    neighborhoods: [
      "Centrum", "Oud-Noord", "Broekhoven", "Zorgvlied", "Reeshof",
      "Berkel-Enschot", "Udenhout", "Goirle", "Oisterwijk", "Hilvarenbeek",
    ],
    reviews: [
      { name: "Familie van den Berg", neighborhood: "Oud-Noord", rating: 5, text: "Oude kast keurig vervangen, netjes gewerkt en heldere uitleg. Top service." },
      { name: "Tom S.", neighborhood: "Reeshof", rating: 5, text: "3-fase kast voor warmtepomp en laadpaal. Prima prijs, alles binnen één dag klaar." },
      { name: "Ellen J.", neighborhood: "Berkel-Enschot", rating: 5, text: "Snelle planning, vriendelijke monteur en een strakke kast als resultaat." },
    ],
    localFaq: [
      { q: "Zijn jullie zegelbevoegd bij Enexis in Tilburg?", a: "Ja. Enexis is de netbeheerder in Tilburg en omstreken. Wij zijn zegelbevoegd, dus er zijn geen extra kosten voor herverzegeling." },
      { q: "Kunnen jullie ook in oudere arbeiderswoningen werken?", a: "Ja. We hebben veel ervaring met oudere woningen in Oud-Noord en Broekhoven en werken met respect voor bestaande afwerking." },
    ],
    installer: {
      name: "Bas",
      years: "2 jaar",
      experience: "1,5 jaar ervaring met groepenkasten",
      bio1: "Bas werkt al 2 jaar bij ons en is begonnen als medewerker binnendienst. Hij merkte al snel dat de hele dag zitten toch niet zijn ding meer was en besloot het werk in het veld te gaan doen.",
      bio2: "\"Die ervaring in de binnendienst helpt me nog steeds. Ik weet hoe het op kantoor afgesproken is en communiceer helder met de klant, terwijl ik netjes hun kast vervang.\"",
    },
  },

  nijmegen: {
    name: "Nijmegen",
    metaTitle: "Groepenkast vervangen Nijmegen | Elvion Group",
    metaDescription:
      "Groepenkast vervangen in Nijmegen door erkende NEN 1010-installateurs. Vaste vanaf-prijs, 15 jaar garantie op AEG en offerte binnen 24 uur.",
    heroIntro:
      "Van vooroorlogse woningen in Bottendaal tot nieuwbouw in Waalsprong: wij vervangen dagelijks groepenkasten in heel Nijmegen. Veilig, snel en klaar voor de energietransitie.",
    body: [
      "Nijmegen is een van de oudste steden van Nederland en dat is terug te zien in de woningvoorraad. In wijken als Bottendaal, Nije Veld en Hunnerberg staan veel woningen uit de wederopbouwperiode, met meterkasten die inmiddels vragen om vervanging.",
      "Onze installateurs zijn zegelbevoegd bij Liander en werken volgens NEN 1010. De vervanging gebeurt in de meeste woningen binnen één werkdag, met beperkte spanningsloze tijd en een keuringsrapport bij oplevering.",
      "In de Waalsprong en Lent zien we juist veel nieuwbouw waar we de kast uitbreiden voor zonnepanelen, warmtepomp of laadpaal. Bij elke vervanging zorgen we voor voldoende reserveruimte om later eenvoudig uit te kunnen breiden.",
    ],
    signals: [
      "Schroef- of smeltzekeringen in de meterkast",
      "Geen (of maar één) aardlekschakelaar",
      "Wens voor warmtepomp, laadpaal of inductie",
      "Hoofdschakelaar slaat regelmatig af",
      "Brandsporen of schroeilucht bij de kast",
      "Verbouwing of dakopbouw in Nijmegen",
    ],
    neighborhoods: [
      "Centrum", "Bottendaal", "Nije Veld", "Hunnerberg", "Hengstdal",
      "Waalsprong", "Lent", "Oosterhout", "Dukenburg", "Lindenholt",
    ],
    reviews: [
      { name: "Familie Hendriks", neighborhood: "Bottendaal", rating: 5, text: "Kast uit de jaren '60 vervangen, alles keurig binnen een dag en direct voorbereid op zonnepanelen." },
      { name: "Wouter B.", neighborhood: "Lent", rating: 5, text: "3-fase kast met voorbereiding voor laadpaal. Prima werk en heldere communicatie." },
      { name: "Sophie L.", neighborhood: "Hunnerberg", rating: 5, text: "Snel gepland en zeer vakkundig gewerkt. Een fijne ervaring." },
    ],
    localFaq: [
      { q: "Wie is de netbeheerder in Nijmegen?", a: "Liander is de netbeheerder in Nijmegen en omstreken. Wij zijn zegelbevoegd bij Liander, dus u betaalt geen extra kosten voor herverzegeling." },
      { q: "Werken jullie ook in oudere Nijmeegse woningen?", a: "Ja. We hebben ruime ervaring met vooroorlogse en wederopbouwwoningen en werken zorgvuldig met bestaande afwerking en bekabeling." },
    ],
    installer: {
      name: "Erwin",
      years: "4 jaar",
      experience: "7 jaar ervaring in de e-tech",
      bio1: "Erwin werkt 4 jaar bij ons en woont in de regio Nijmegen. Van karakteristieke woningen in Bottendaal tot moderne gezinshuizen in de Waalsprong, elk klusje pakt hij met dezelfde precisie aan.",
      bio2: "\"Ik hou van goed voorbereid werk. Even rustig kijken, uitleg geven, en dan pas beginnen. Aan het eind moet de klant écht begrijpen wat er in de kast zit.\"",
    },
  },

  arnhem: {
    name: "Arnhem",
    metaTitle: "Groepenkast vervangen Arnhem | Elvion Group",
    metaDescription:
      "Groepenkast vervangen in Arnhem door erkende NEN 1010-installateurs. Vaste vanaf-prijs, 15 jaar garantie op AEG en offerte binnen 24 uur.",
    heroIntro:
      "Van jaren-'30 woningen in Sint Marten tot nieuwbouw in Schuytgraaf: wij vervangen groepenkasten in heel Arnhem. Veilig, snel en volgens NEN 1010.",
    body: [
      "Arnhem heeft een gevarieerde woningvoorraad, met veel karakteristieke woningen in Sint Marten, Sonsbeekkwartier en Klarendal. In deze wijken vinden we regelmatig verouderde meterkasten die niet meer voldoen aan de huidige norm.",
      "Onze installateurs zijn zegelbevoegd bij Liander en werken volgens NEN 1010. We vervangen de kast doorgaans in één werkdag, met een vaste prijs vooraf en een keuringsrapport na oplevering.",
      "In Schuytgraaf en de Rijnwijken zien we vaak vraag naar uitbreiding voor zonnepanelen, warmtepomp of laadpaal. Bij elke vervanging plaatsen we een AEG-kast met reserveruimte, zodat toekomstige uitbreiding zonder hakwerk kan.",
    ],
    signals: [
      "Kast met schroef- of smeltzekeringen",
      "Geen (of maar één) aardlekschakelaar",
      "Wens voor laadpaal, warmtepomp of inductie",
      "Hoofdschakelaar slaat regelmatig af",
      "Brandsporen of schroeilucht bij de kast",
      "Verbouwing of dakopbouw in uw Arnhemse woning",
    ],
    neighborhoods: [
      "Centrum", "Sint Marten", "Sonsbeekkwartier", "Klarendal", "Spijkerkwartier",
      "Schuytgraaf", "Presikhaaf", "Malburgen", "Elden", "Velp",
    ],
    reviews: [
      { name: "Familie Vermeer", neighborhood: "Sint Marten", rating: 5, text: "Oude meterkast keurig vervangen, netjes gewerkt en alles duidelijk uitgelegd. Zeer tevreden." },
      { name: "Mark T.", neighborhood: "Schuytgraaf", rating: 5, text: "Kast voorbereid op zonnepanelen en laadpaal. Vaste prijs, snelle uitvoering." },
      { name: "Ingrid P.", neighborhood: "Sonsbeekkwartier", rating: 5, text: "Nette monteur, keurig werk en fijn contact vooraf. Aanrader in Arnhem." },
    ],
    localFaq: [
      { q: "Zijn jullie zegelbevoegd bij Liander in Arnhem?", a: "Ja. Liander is de netbeheerder in Arnhem en Gelderland. Wij zijn zegelbevoegd, dus er zijn geen extra kosten voor herverzegeling." },
      { q: "Werken jullie ook in monumentale panden in het centrum?", a: "Ja. We hebben veel ervaring met karakteristieke panden en werken zorgvuldig, met respect voor bestaande afwerking." },
    ],
    installer: {
      name: "Kevin",
      years: "3,5 jaar",
      experience: "15 jaar ervaring met groepenkasten",
      bio1: "Kevin werkt 3,5 jaar bij ons en woont in de regio Arnhem. Van karakteristieke woningen in Klarendal tot nieuwbouw in Schuytgraaf, hij is thuis in elke Arnhemse wijk.",
      bio2: "\"Wat ik mooi vind aan dit vak? Dat je iemands huis écht veiliger achterlaat dan je het aantrof. En natuurlijk klaar voor zonnepanelen en een laadpaal, want daar krijg ik nu bijna elke week wel een vraag over.\"",
    },
  },
} satisfies Record<string, CityContent>;

export type CitySlug = keyof typeof CITY_CONTENT;

// ---- Gedeelde content (per stad hergebruikt) ----

export const GENERIC_FAQ: CityFaq[] = [
  { q: "Wat kost het vervangen van een groepenkast?", a: "Een standaard A-Merk AEG 1-fase kast met 6 groepen al geïnstalleerd vanaf € 510 Incl. btw, inclusief materialen en montage. De uiteindelijke prijs hangt af van het aantal groepen, 1-fase of 3-fase, en de staat van uw meterkast. U ontvangt vooraf een vaste offerte, geen verrassingen achteraf." },
  { q: "Hoe lang duurt het vervangen van een groepenkast?", a: "In de meeste woningen is de kast dezelfde week nog vervangen wanneer dit voor uw planning ook uitkomt, doorgaans vind de installatie binnen 3 tot 6 werkdagen na akkoord plaats. U bent slechts kort zonder stroom, gemiddeld zijn onze ervaren monteurs 1 tot 1.5 bij u bezig." },
  { q: "Wat is NEN 1010?", a: "NEN 1010 is de wettelijke norm voor laagspanningsinstallaties in Nederland. Iedere nieuwe groepenkast moet aan deze norm voldoen. Onze installateurs zijn NEN 1010-gecertificeerd en leveren de installatie inclusief keuringsrapport." },
  { q: "Waarom kiezen jullie voor AEG-groepenkasten?", a: "Wij monteren AEG omdat het een topmerk is met bewezen betrouwbaarheid. U krijgt 15 jaar garantie op de werking en 10 jaar op de normering." },
  { q: "Krijg ik ook garantie op het werk zelf?", a: "Ja. Naast de fabrieksgarantie van AEG geven we 2 jaar installatiegarantie op ons eigen werk." },
  { q: "Moet ik een aardlekschakelaar hebben?", a: "Ja. Sinds 1975 is een aardlekschakelaar verplicht in Nederlandse woningen. In moderne kasten gebruiken wij aardlekautomaten per groep, dat is veiliger dan één centrale aardlekschakelaar." },
  { q: "Krijg ik de meterkast voorbereid op zonnepanelen of laadpaal?", a: "Ja. Wij plaatsen standaard extra ruimte en bekabelingsvoorbereiding. Zo scheelt het achteraf tot 65% ten opzichte van later bijplaatsen." },
  { q: "Wanneer moet ik overstappen naar 3-fase?", a: "Bij een combinatie van inductie, warmtepomp, laadpaal en/of veel zonnepanelen wordt 1-fase krap. 3-fase geeft meer vermogen en voorkomt overbelasting. We adviseren u vrijblijvend." },
  { q: "Zit het zegelrecht bij Liander/Stedin inbegrepen?", a: "Ja. Onze installateurs zijn zegelbevoegd bij zowel Liander als Stedin, u betaalt geen aparte kosten voor herverzegeling." },
  { q: "Kan ik subsidie krijgen op een nieuwe groepenkast?", a: "Voor de kast zelf zijn er geen landelijke subsidies, maar wanneer u tegelijk isoleert, een warmtepomp of laadpaal aansluit, kunt u vaak wél gebruikmaken van ISDE- of gemeentelijke subsidies. We adviseren u graag." },
  { q: "Wat als er iets niet in orde blijkt in de meterkast?", a: "Dat merken we tijdens de opname. U ontvangt vooraf een eerlijk advies en een vaste prijs, meerwerk gebeurt alleen na uw expliciete akkoord." },
  { q: "Kan ik ook alleen advies krijgen zonder direct vervanging?", a: "Zeker. Een adviesbezoek is vrijblijvend, en u zit nergens aan vast." },
];
